import { determineGateValueShared, determineGateVariantIndex, getFeature, } from "gather-gates/dist/src/public/gate";
import { getFeatures, GatedFeatName, } from "gather-client-common/dist/src/public/gatekeeping/FEATURES";
import { LocalPreferenceKeys, localPreferences } from "../LocalPreferences";
import { GatedVariantSetName, isValidVariantFromString, getVariantSets, } from "./variantSets";
import { memoize } from "lodash";
import { getSelectedOrRawLocale } from "../i18n";
import { isRWTemplateSpace } from "gather-common/dist/src/public/resources/remoteWorkOnboarding";
import { getAllGateValuesForLogging as _getAllGateValuesForLogging, } from "gather-client-common/dist/src/public/metrics/getSpaceEventProperties";
import { enumFromValue, objectEntries } from "gather-common/dist/src/public/ts-utils";
import { equals } from "ramda";
import { switchEnv } from "gather-env-config/dist/src/public/env";
import { isNotNil } from "gather-common/dist/src/public/fpHelpers";
import { getIsMobile } from "../deviceDetect";
import { getGatingStateController } from "./getGatingStateController";
import { GatingStateKeys } from "gather-client-common/dist/src/public/gatekeeping/GatingStateController";
const featureGateOverrides = localPreferences.get(LocalPreferenceKeys.FeatureGateOverrides) || [];
const featureGateDisableOverrides = localPreferences.get(LocalPreferenceKeys.FeatureGateDisableOverrides) || [];
const variantGateOverrides = localPreferences.get(LocalPreferenceKeys.VariantGateOverrides) || {};
const checkAndPersistGateToLocalPreferences = (featName, feature) => {
    if (!feature.space &&
        feature.persistGateToLocalPreferences &&
        !featureGateOverrides.includes(featName)) {
        featureGateOverrides.push(featName);
        localPreferences.set(LocalPreferenceKeys.FeatureGateOverrides, featureGateOverrides);
    }
};
const getVariantValueFromLocalPreferences = (variantSetName) => {
    const variantValue = variantGateOverrides[variantSetName];
    if (!variantValue || !isValidVariantFromString(variantSetName, variantValue))
        return undefined;
    return variantValue;
};
const checkVariantGateFromLocalPreferences = (variantSetName, variantValue) => variantGateOverrides[variantSetName] === variantValue;
const checkAndPersistVariantGateToLocalPreferences = (variantSetName, variantValue) => {
    const variantSet = getVariantSets()[variantSetName];
    if (variantSet.persistGateToLocalPreferences &&
        variantSet.enabled &&
        !getVariantValueFromLocalPreferences(variantSetName)) {
        variantGateOverrides[variantSetName] = variantValue;
        localPreferences.set(LocalPreferenceKeys.VariantGateOverrides, {
            ...variantGateOverrides,
            [variantSetName]: variantValue,
        });
    }
};
export const shouldForceEnableOnLocalEnv = (enableOnLocalEnvSetting) => switchEnv({
    prod: () => false,
    staging: () => false,
    dev: () => false,
    local: () => enableOnLocalEnvSetting,
    test: () => false,
});
export const getForceSetGateForTestingEnv = (exposure) => switchEnv({
    prod: () => undefined,
    staging: () => undefined,
    dev: () => undefined,
    local: () => undefined,
    test: () => exposure === 1,
});
export const getForceSetVariantForTestingEnv = () => switchEnv({
    prod: () => false,
    staging: () => false,
    dev: () => false,
    local: () => false,
    test: () => true,
});
export const customBrowserGateChecks = (featName, feature, gateFactors) => {
    if (featureGateDisableOverrides.includes(featName))
        return { matched: true, result: false };
    if (featureGateOverrides.includes(featName))
        return { matched: true, result: true };
    const forceSetForTestingEnvValue = getForceSetGateForTestingEnv(feature.exposure);
    if (isNotNil(forceSetForTestingEnvValue))
        return { matched: true, result: forceSetForTestingEnvValue };
    const { spaceId } = gateFactors;
    if (feature.blacklistRWTemplates && isRWTemplateSpace(spaceId || ""))
        return { matched: true, result: false };
    if (feature.blacklistLocales) {
        const isBlacklisted = feature.blacklistLocales.some((blacklistedLocale) => blacklistedLocale === getSelectedOrRawLocale());
        if (isBlacklisted)
            return { matched: true, result: false };
    }
    if (shouldForceEnableOnLocalEnv(Boolean(feature.enableOnLocalEnv)))
        return { matched: true, result: true };
    if (feature.enableForBeta) {
        const betaFeaturesEnabled = getGatingStateController().get(GatingStateKeys.BetaFeaturesEnabled);
        if (betaFeaturesEnabled.includes(featName))
            return { matched: true, result: true };
    }
    return { matched: false };
};
const determineGateValue = (featName, feature, gateFactors) => determineGateValueShared(featName, feature, gateFactors, () => customBrowserGateChecks(featName, feature, gateFactors));
export const gate = (featName, userId, spaceId, spaceCreationDate) => {
    const feature = getFeature(getFeatures(), featName);
    if (!feature)
        return false;
    const gateValue = determineGateValue(featName, feature, { userId, spaceId, spaceCreationDate });
    if (gateValue) {
        checkAndPersistGateToLocalPreferences(featName, feature);
    }
    return gateValue;
};
export const determineGateVariantIndexWithSettings = (variantSetName, uniqueGatePropId) => {
    const variantSet = getVariantSets()[variantSetName];
    const possibleVariantSetValueFromLocalPreferences = getVariantValueFromLocalPreferences(variantSetName);
    if (possibleVariantSetValueFromLocalPreferences) {
        return variantSet.variants.indexOf(possibleVariantSetValueFromLocalPreferences);
    }
    if (getForceSetVariantForTestingEnv())
        return 0;
    const possibleOverride = variantSet.overrides?.[uniqueGatePropId];
    if (possibleOverride)
        return variantSet.variants.indexOf(possibleOverride);
    const isBlacklisted = variantSet.blacklistLocales
        ? variantSet.blacklistLocales.some(equals(getSelectedOrRawLocale()))
        : false;
    const isDisabledDueToMobileWeb = variantSet.disableOnMobileWeb && getIsMobile();
    if (isBlacklisted ||
        isDisabledDueToMobileWeb ||
        (!variantSet.enabled && !shouldForceEnableOnLocalEnv(Boolean(variantSet.enableOnLocalEnv)))) {
        return 0;
    }
    const determinedVariantIndex = determineGateVariantIndex(variantSetName, variantSet.variants.length, uniqueGatePropId);
    const determinedVariantValue = variantSet.variants[determinedVariantIndex];
    if (determinedVariantValue) {
        checkAndPersistVariantGateToLocalPreferences(variantSetName, determinedVariantValue);
    }
    return determinedVariantIndex;
};
export const gateVariantBase = (variantSetName, variant, uniqueGatePropId) => {
    const variantSet = getVariantSets()[variantSetName];
    if (checkVariantGateFromLocalPreferences(variantSetName, variant))
        return true;
    const variantIndex = variantSet.variants.indexOf(variant);
    const determinedIndex = determineGateVariantIndexWithSettings(variantSetName, uniqueGatePropId);
    return variantIndex === determinedIndex;
};
export const getAllGateValuesForLogging = (userId, spaceId, spaceCreationDate) => _getAllGateValuesForLogging(userId, spaceId, spaceCreationDate, gate);
export const getAllVariantSetValuesForLogging = memoize((userId, spaceId) => Object.entries(getVariantSets()).reduce((allVariantSets, [variantSetName, variantSet]) => {
    const spaceIdOrUserId = variantSet.space ? spaceId : userId;
    if (!spaceIdOrUserId)
        return allVariantSets;
    const gateVariantIndex = determineGateVariantIndexWithSettings(enumFromValue(variantSetName, GatedVariantSetName), spaceIdOrUserId);
    return {
        ...allVariantSets,
        [variantSetName]: variantSet.variants[gateVariantIndex] || "[Unknown Variant]",
    };
}, {}), (userId, spaceId) => `${userId || ""}-${spaceId || ""}`);
export function getAllGateNames() {
    return Object.keys(getFeatures());
}
export function getAllVariantSetNames() {
    return Object.keys(getVariantSets());
}
export function getAllBetaFeatureGateNames() {
    return objectEntries(getFeatures())
        .filter(([_featName, feat]) => feat.enableForBeta)
        .map(([featName]) => enumFromValue(featName, GatedFeatName));
}
//# sourceMappingURL=gate.js.map